import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { tabletMin } from '../../constants';

const ANIMATION_DURATION = 200;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const scaleOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(0.5rem);
  }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const slideDown = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
`;

export { ANIMATION_DURATION };

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
  z-index: 20;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${({ closing }) => (closing ? fadeOut : fadeIn)} ${ANIMATION_DURATION}ms ease-out forwards;

  @media ${tabletMin} {
    align-items: center;
  }
`;

export const ModalCard = styled.div.attrs({ 'data-track-view': true })`
  background: #ffffff;
  width: 100%;
  border-radius: ${rem(12)} ${rem(12)} 0 0;
  box-shadow: 0 ${rem(-10)} ${rem(15)} ${rem(-3)} rgba(0, 0, 0, 0.1),
    0 ${rem(-4)} ${rem(6)} ${rem(-2)} rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  animation: ${({ closing }) => (closing ? slideDown : slideUp)} ${ANIMATION_DURATION}ms cubic-bezier(0.16, 1, 0.3, 1) forwards;

  /* Focused programmatically on open, so no focus ring. */
  &:focus {
    outline: none;
  }

  @media ${tabletMin} {
    border: 1px solid #ffffff;
    border-radius: ${rem(12)};
    box-shadow: 0 ${rem(25)} ${rem(50)} ${rem(-5)} rgba(0, 0, 0, 0.25);
    width: ${rem(390)};
    max-width: calc(100vw - ${rem(32)});
    padding: ${rem(24)} ${rem(32)} ${rem(32)};
    gap: ${rem(24)};
    align-items: flex-end;
    animation: ${({ closing }) => (closing ? scaleOut : scaleIn)} ${ANIMATION_DURATION}ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
`;

export const SheetHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media ${tabletMin} {
    display: none;
  }
`;

export const SheetTopBar = styled.div`
  height: ${rem(12)};
  width: 100%;
  background: #ffffff;
  border-radius: ${rem(12)} ${rem(12)} 0 0;
`;

export const SheetTitleRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 ${rem(16)};
  width: 100%;
  background: #ffffff;
`;

export const SheetTitleSpacer = styled.div`
  width: ${rem(32)};
  height: ${rem(32)};
  flex-shrink: 0;
`;

export const SheetTitle = styled.p`
  flex: 1;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: ${rem(16)};
  line-height: ${rem(24)};
  color: #252c34;
  text-align: left;
  margin: 0;
  min-width: 0;
`;

export const SheetCloseButton = styled.button`
  width: ${rem(32)};
  height: ${rem(32)};
  padding: ${rem(10)};
  background: #f8f8f9;
  border: none;
  border-radius: ${rem(16)};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  &:hover {
    background: #e2e4e7;
  }
`;

export const SheetBottomBar = styled.div`
  height: ${rem(12)};
  width: 100%;
  background: #ffffff;
`;

export const SheetContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};
  padding: 0 ${rem(16)} ${rem(32)};
  background: #ffffff;

  @media ${tabletMin} {
    display: none;
  }
`;

export const SheetDescription = styled.p`
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  color: #384553;
  text-align: left;
  margin: 0;
  white-space: pre-line;
  width: 100%;
`;

export const TopRow = styled.div`
  display: none;

  @media ${tabletMin} {
    display: flex;
    flex-direction: column;
    gap: ${rem(16)};
    width: 100%;
    position: relative;
  }
`;

export const ModalTitle = styled.p`
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: ${rem(20)};
  line-height: ${rem(32)};
  color: #252c34;
  margin: 0;
  padding-right: ${rem(40)};
`;

export const ModalDescription = styled.p`
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: ${rem(16)};
  line-height: ${rem(24)};
  color: #384553;
  margin: 0;
  white-space: pre-line;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: ${rem(32)};
  height: ${rem(32)};
  padding: ${rem(8)};
  background: #f8f8f9;
  border: none;
  border-radius: ${rem(6)};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &:hover {
    background: #e2e4e7;
  }
`;

export const Divider = styled.div`
  display: none;
  width: 100%;
  height: 1px;
  background: #e2e4e7;
  border-radius: ${rem(2)};

  @media ${tabletMin} {
    display: block;
  }
`;

export const SheetDivider = styled.div`
  width: ${rem(320)};
  height: 1px;
  background: #e2e4e7;
`;

export const PlansRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  gap: ${rem(16)};
  width: 100%;
  @media ${tabletMin} {
    justify-content: flex-start;
  }
`;

export const DesktopPlansRow = styled(PlansRow)`
  display: none;

  @media ${tabletMin} {
    display: flex;
  }
`;

export const PlansLabel = styled.span`
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  color: #384553;
  white-space: nowrap;
`;

export const PlanBadge = styled.span<{ color: string }>`
  display: flex;
  align-items: center;
  gap: ${rem(2)};
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  color: ${({ color }) => color};
  white-space: nowrap;
`;

export const AllPlansText = styled.span`
  font-size: ${rem(12)};
  font-weight: 500;
  color: ${({ theme }) => theme.color.ui.grey[90]};
`;
